import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import LandingNav from '../../components/landing/LandingNav';
import LandingFooter from '../../components/landing/LandingFooter';
import FreightCalculator from '../../components/landing/FreightCalculator';

// ── Dados de conteúdo ─────────────────────────────────────────

const FEATURES = [
  {
    icon: '⚡',
    title: 'Cotação em tempo real',
    desc: 'Compare preços e prazos de todas as transportadoras em menos de 300ms, sem travar o checkout do seu e-commerce.',
  },
  {
    icon: '🏷️',
    title: 'Emissão de Etiquetas',
    desc: 'Gere etiquetas em PDF ou ZPL para múltiplos pedidos de uma vez. Impressão em massa e PLP automática.',
  },
  {
    icon: '📍',
    title: 'Rastreamento Centralizado',
    desc: 'Um painel único para acompanhar todos os envios, independente da transportadora.',
  },
  {
    icon: '🔄',
    title: 'Logística Reversa',
    desc: 'Autorize postagens reversas para o consumidor final com poucos cliques.',
  },
  {
    icon: '🔗',
    title: 'Integração com 1 clique',
    desc: 'Conecte Nuvemshop, Shopify, WooCommerce e ERPs como Bling e Tiny sem escrever uma linha de código.',
  },
  {
    icon: '🛡️',
    title: 'Fallback inteligente',
    desc: 'Se o webservice de uma transportadora cair, o sistema continua cotando via tabela local em cache.',
  },
  {
    icon: '📊',
    title: 'Dashboard Logístico',
    desc: 'Acompanhe SLA de entregas, gastos mensais, atrasos e taxa de entrega no prazo em tempo real.',
  },
  {
    icon: '💼',
    title: 'Gestão Financeira',
    desc: 'Carteira virtual com recarga via PIX, Boleto ou Cartão. Bloqueio automático de etiquetas por saldo.',
  },
];

const CARRIERS = [
  { name: 'Correios', tag: 'PAC · SEDEX · Mini Envios' },
  { name: 'Jadlog', tag: '.Package · .Com · .Expresso' },
  { name: 'Loggi', tag: 'Expresso · Econômico' },
  { name: 'J&T Express', tag: 'Standard · Express' },
  { name: 'Melhor Envio', tag: 'Multi-carrier' },
  { name: 'Azul Cargo', tag: 'Aéreo · Rodoviário' },
];

const INTEGRATIONS = [
  { name: 'Nuvemshop', color: '#00c8b0' },
  { name: 'Shopify', color: '#96bf48' },
  { name: 'WooCommerce', color: '#7f54b3' },
  { name: 'Bling', color: '#0057b8' },
  { name: 'Tiny ERP', color: '#f97316' },
  { name: 'API REST', color: '#374151' },
];

const PLANS = [
  {
    name: 'Starter',
    price: 'R$ 99',
    period: '/mês',
    desc: 'Ideal para lojas que estão começando',
    features: [
      'Até 200 envios/mês',
      'Correios e Jadlog',
      'Dashboard básico',
      'Suporte por e-mail',
      'API com 1 token',
    ],
    cta: 'Começar grátis',
    highlight: false,
  },
  {
    name: 'Pro',
    price: 'R$ 249',
    period: '/mês',
    desc: 'Para lojas em crescimento acelerado',
    features: [
      'Até 1.000 envios/mês',
      'Todas as transportadoras',
      'Dashboard avançado (BI)',
      'Suporte prioritário',
      'Tokens ilimitados',
      'Logística reversa',
      'Webhooks',
    ],
    cta: 'Assinar Pro',
    highlight: true,
  },
  {
    name: 'Enterprise',
    price: 'Sob consulta',
    period: '',
    desc: 'Para grandes operações logísticas',
    features: [
      'Envios ilimitados',
      'Tudo do Pro',
      'Contrato próprio',
      'Auditoria de faturas',
      'SLA dedicado',
      'Gerente de conta',
      'On-premise disponível',
    ],
    cta: 'Falar com vendas',
    highlight: false,
  },
];

const STATS = [
  { value: '99,9%', label: 'Uptime garantido' },
  { value: '<300ms', label: 'Tempo de cotação' },
  { value: '6+', label: 'Transportadoras' },
  { value: '3', label: 'Plataformas integradas' },
];

// ── Componente principal ───────────────────────────────────────

export default function LandingPage() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactSent, setContactSent] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) return;
    // Pequeno delay para garantir que a página renderizou
    const timer = setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [location.hash]);

  function handleContact(e) {
    e.preventDefault();
    setContactSent(true);
  }

  return (
    <div style={styles.page}>
      <LandingNav />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section style={styles.hero}>
        <div style={styles.heroBg} />
        <div style={styles.heroContent}>
          <div style={styles.heroBadge}>
            🚀 Nova plataforma logística para e-commerces brasileiros
          </div>
          <h1 style={styles.heroTitle}>
            Cotação de frete de todas<br />
            as transportadoras em<br />
            <span style={styles.heroHighlight}>um único lugar</span>
          </h1>
          <p style={styles.heroDesc}>
            Conecte seu e-commerce a Correios, Jadlog, Loggi e muito mais.
            Reduza custos logísticos, automatize etiquetas e rastreie todos
            os seus envios em um painel centralizado.
          </p>
          <div style={styles.heroCtas}>
            <a href="/login" style={styles.heroCtaPrimary}>
              Criar conta grátis
            </a>
            <button style={styles.heroCtaSecondary} onClick={() => document.getElementById('calculadora')?.scrollIntoView({ behavior: 'smooth' })}>
              Ver demonstração →
            </button>
          </div>
          <div style={styles.heroStats}>
            {STATS.map((s) => (
              <div key={s.value} style={styles.heroStat}>
                <div style={styles.heroStatValue}>{s.value}</div>
                <div style={styles.heroStatLabel}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRANSPORTADORAS ──────────────────────────────────── */}
      <section style={styles.carriersStrip}>
        <div style={styles.sectionInner}>
          <p style={styles.carriersLabel}>Transportadoras integradas</p>
          <div style={styles.carriersGrid}>
            {CARRIERS.map((c) => (
              <div key={c.name} style={styles.carrierChip}>
                <strong>{c.name}</strong>
                <span style={styles.carrierTag}>{c.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FUNCIONALIDADES ──────────────────────────────────── */}
      <section id="funcionalidades" style={styles.section}>
        <div style={styles.sectionInner}>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionBadge}>Funcionalidades</span>
            <h2 style={styles.sectionTitle}>Tudo que você precisa para<br />escalar sua logística</h2>
            <p style={styles.sectionDesc}>
              Do checkout ao rastreamento final, o BringHub Frete cobre toda a jornada logística do seu e-commerce.
            </p>
          </div>
          <div style={styles.featuresGrid}>
            {FEATURES.map((f) => (
              <div key={f.title} style={styles.featureCard}>
                <div style={styles.featureIcon}>{f.icon}</div>
                <h3 style={styles.featureTitle}>{f.title}</h3>
                <p style={styles.featureDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INTEGRAÇÕES ──────────────────────────────────────── */}
      <section id="integrações" style={{ ...styles.section, background: '#f8fafc' }}>
        <div style={styles.sectionInner}>
          <div style={styles.integrationLayout}>
            <div style={styles.integrationText}>
              <span style={styles.sectionBadge}>Integrações</span>
              <h2 style={styles.sectionTitle}>Conecte sua loja em<br />segundos</h2>
              <p style={styles.sectionDesc}>
                Plugins nativos para as principais plataformas de e-commerce do Brasil. Ou use nossa API REST para uma integração totalmente customizada.
              </p>
              <ul style={styles.integrationList}>
                {[
                  'Ativação com 1 clique — sem código',
                  'Webhooks automáticos de rastreamento',
                  'API com autenticação Bearer Token',
                  'Documentação Swagger completa',
                ].map((item) => (
                  <li key={item} style={styles.integrationItem}>
                    <span style={styles.checkIcon}>✓</span> {item}
                  </li>
                ))}
              </ul>
              <a href="/login" style={styles.integrationCta}>Ver documentação da API →</a>
            </div>
            <div style={styles.integrationCards}>
              {INTEGRATIONS.map((int) => (
                <div key={int.name} style={{ ...styles.integrationCard, borderTopColor: int.color }}>
                  <div style={{ ...styles.integrationDot, background: int.color }} />
                  <span style={styles.integrationName}>{int.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CALCULADORA ──────────────────────────────────────── */}
      <section id="calculadora" style={styles.section}>
        <div style={styles.sectionInner}>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionBadge}>Calculadora</span>
            <h2 style={styles.sectionTitle}>Teste agora, sem cadastro</h2>
            <p style={styles.sectionDesc}>
              Informe origem, destino e peso para ver as opções de frete disponíveis.
            </p>
          </div>
          <div style={styles.calcCard}>
            <FreightCalculator />
          </div>
        </div>
      </section>

      {/* ── PLANOS ───────────────────────────────────────────── */}
      <section id="planos" style={{ ...styles.section, background: '#f8fafc' }}>
        <div style={styles.sectionInner}>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionBadge}>Planos</span>
            <h2 style={styles.sectionTitle}>Preço simples e transparente</h2>
            <p style={styles.sectionDesc}>
              Comece grátis por 14 dias. Sem cartão de crédito.
            </p>
          </div>
          <div style={styles.plansGrid}>
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                style={{ ...styles.planCard, ...(plan.highlight ? styles.planCardHighlight : {}) }}
              >
                {plan.highlight && <div style={styles.planPopular}>Mais popular</div>}
                <div style={styles.planName}>{plan.name}</div>
                <div style={styles.planPriceRow}>
                  <span style={{ ...styles.planPrice, ...(plan.highlight ? styles.planPriceWhite : {}) }}>
                    {plan.price}
                  </span>
                  <span style={styles.planPeriod}>{plan.period}</span>
                </div>
                <p style={{ ...styles.planDesc, ...(plan.highlight ? styles.planDescWhite : {}) }}>
                  {plan.desc}
                </p>
                <ul style={styles.planFeatures}>
                  {plan.features.map((f) => (
                    <li key={f} style={{ ...styles.planFeature, ...(plan.highlight ? styles.planFeatureWhite : {}) }}>
                      <span style={{ ...styles.planCheck, ...(plan.highlight ? styles.planCheckWhite : {}) }}>✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href="/login"
                  style={{ ...styles.planCta, ...(plan.highlight ? styles.planCtaHighlight : {}) }}
                >
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ────────────────────────────────────────── */}
      <section style={styles.ctaSection}>
        <div style={styles.sectionInner}>
          <div style={styles.ctaBox}>
            <h2 style={styles.ctaTitle}>Pronto para otimizar sua logística?</h2>
            <p style={styles.ctaDesc}>
              Junte-se a centenas de lojistas que economizam tempo e dinheiro com o BringHub Frete.
            </p>
            <div style={styles.ctaBtns}>
              <a href="/login" style={styles.ctaBtnPrimary}>Criar conta grátis</a>
              <button
                style={styles.ctaBtnSecondary}
                onClick={() => document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Falar com especialista
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTATO ──────────────────────────────────────────── */}
      <section id="contato" style={styles.section}>
        <div style={styles.sectionInner}>
          <div style={styles.contactLayout}>
            <div style={styles.contactInfo}>
              <span style={styles.sectionBadge}>Contato</span>
              <h2 style={styles.sectionTitle}>Fale com nosso time</h2>
              <p style={styles.sectionDesc}>
                Tire suas dúvidas, solicite uma demonstração ou fale sobre planos Enterprise.
              </p>
              <div style={styles.contactItems}>
                {[
                  { icon: '📧', label: 'E-mail', value: 'contato@bringhubfrete.com.br' },
                  { icon: '💬', label: 'WhatsApp', value: '(11) 99999-9999' },
                  { icon: '🕐', label: 'Atendimento', value: 'Seg–Sex, 9h às 18h' },
                ].map((item) => (
                  <div key={item.label} style={styles.contactItem}>
                    <span style={styles.contactItemIcon}>{item.icon}</span>
                    <div>
                      <div style={styles.contactItemLabel}>{item.label}</div>
                      <div style={styles.contactItemValue}>{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={styles.contactFormCard}>
              {contactSent ? (
                <div style={styles.contactSuccess}>
                  <span style={styles.contactSuccessIcon}>🎉</span>
                  <h3 style={styles.contactSuccessTitle}>Mensagem enviada!</h3>
                  <p style={styles.contactSuccessDesc}>Em breve nossa equipe entrará em contato.</p>
                </div>
              ) : (
                <form onSubmit={handleContact} style={styles.contactForm} noValidate>
                  <h3 style={styles.contactFormTitle}>Envie uma mensagem</h3>
                  <div style={styles.contactField}>
                    <label style={styles.contactLabel}>Nome</label>
                    <input
                      style={styles.contactInput}
                      value={contactForm.name}
                      onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="João Silva"
                      required
                    />
                  </div>
                  <div style={styles.contactField}>
                    <label style={styles.contactLabel}>E-mail</label>
                    <input
                      style={styles.contactInput}
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="joao@minhaloja.com.br"
                      required
                    />
                  </div>
                  <div style={styles.contactField}>
                    <label style={styles.contactLabel}>Mensagem</label>
                    <textarea
                      style={{ ...styles.contactInput, minHeight: '100px', resize: 'vertical' }}
                      value={contactForm.message}
                      onChange={(e) => setContactForm((f) => ({ ...f, message: e.target.value }))}
                      placeholder="Como podemos ajudar?"
                      required
                    />
                  </div>
                  <button type="submit" style={styles.contactSubmit}>
                    Enviar mensagem
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}

// ── Estilos ───────────────────────────────────────────────────

const styles = {
  page: { fontFamily: 'Inter, sans-serif', background: '#fff', color: '#111827' },

  // Hero
  hero: {
    position: 'relative',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1e40af 100%)',
    padding: '120px 24px 80px',
  },
  heroBg: {
    position: 'absolute', inset: 0,
    background: 'radial-gradient(ellipse at 70% 40%, rgba(59,130,246,0.25) 0%, transparent 60%)',
    pointerEvents: 'none',
  },
  heroContent: {
    position: 'relative',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.15)',
    color: '#93c5fd',
    fontSize: '13px',
    fontWeight: '500',
    padding: '6px 14px',
    borderRadius: '20px',
    marginBottom: '28px',
    backdropFilter: 'blur(8px)',
  },
  heroTitle: {
    fontSize: 'clamp(36px, 5vw, 64px)',
    fontWeight: '800',
    color: '#ffffff',
    lineHeight: 1.1,
    letterSpacing: '-1.5px',
    marginBottom: '24px',
  },
  heroHighlight: {
    background: 'linear-gradient(90deg, #60a5fa, #34d399)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  heroDesc: {
    fontSize: '18px',
    color: 'rgba(255,255,255,0.7)',
    lineHeight: 1.7,
    maxWidth: '560px',
    marginBottom: '36px',
  },
  heroCtas: { display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '56px' },
  heroCtaPrimary: {
    padding: '14px 28px',
    background: '#f97316',
    color: '#fff',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '700',
    textDecoration: 'none',
    letterSpacing: '0.2px',
  },
  heroCtaSecondary: {
    padding: '14px 28px',
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    backdropFilter: 'blur(8px)',
  },
  heroStats: {
    display: 'flex',
    gap: '40px',
    paddingTop: '32px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    flexWrap: 'wrap',
  },
  heroStat: {},
  heroStatValue: { fontSize: '28px', fontWeight: '800', color: '#fff', letterSpacing: '-0.5px' },
  heroStatLabel: { fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' },

  // Carriers Strip
  carriersStrip: {
    background: '#f8fafc',
    padding: '32px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  carriersLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '16px',
    textAlign: 'center',
  },
  carriersGrid: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '10px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  carrierChip: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
    padding: '10px 20px',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    fontSize: '14px',
    color: '#374151',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  carrierTag: { fontSize: '11px', color: '#9ca3af' },

  // Section base
  section: { padding: '96px 24px' },
  sectionInner: { maxWidth: '1200px', margin: '0 auto' },
  sectionHeader: { textAlign: 'center', maxWidth: '640px', margin: '0 auto 56px' },
  sectionBadge: {
    display: 'inline-block',
    background: '#eff6ff',
    color: '#1e40af',
    fontSize: '12px',
    fontWeight: '700',
    padding: '4px 12px',
    borderRadius: '20px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '14px',
  },
  sectionTitle: {
    fontSize: 'clamp(24px, 3vw, 40px)',
    fontWeight: '800',
    color: '#111827',
    letterSpacing: '-0.8px',
    lineHeight: 1.2,
    marginBottom: '14px',
  },
  sectionDesc: { fontSize: '16px', color: '#6b7280', lineHeight: 1.7 },

  // Features
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '20px',
  },
  featureCard: {
    background: '#fff',
    border: '1px solid #f3f4f6',
    borderRadius: '14px',
    padding: '28px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    transition: 'transform 0.15s, box-shadow 0.15s',
  },
  featureIcon: { fontSize: '30px', marginBottom: '14px' },
  featureTitle: { fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '8px' },
  featureDesc: { fontSize: '14px', color: '#6b7280', lineHeight: 1.6 },

  // Integrations
  integrationLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '64px',
    alignItems: 'center',
  },
  integrationText: {},
  integrationList: { listStyle: 'none', padding: 0, margin: '20px 0 28px', display: 'flex', flexDirection: 'column', gap: '12px' },
  integrationItem: { fontSize: '15px', color: '#374151', display: 'flex', alignItems: 'center', gap: '10px' },
  checkIcon: { color: '#16a34a', fontWeight: '700', fontSize: '16px' },
  integrationCta: { color: '#1e40af', fontWeight: '600', fontSize: '15px', textDecoration: 'none' },
  integrationCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
  },
  integrationCard: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderTop: '3px solid #e5e7eb',
    borderRadius: '10px',
    padding: '18px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  integrationDot: {
    width: '10px', height: '10px',
    borderRadius: '50%',
  },
  integrationName: { fontSize: '13px', fontWeight: '600', color: '#374151' },

  // Calculator
  calcCard: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '16px',
    padding: '40px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    maxWidth: '800px',
    margin: '0 auto',
  },

  // Plans
  plansGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px',
    alignItems: 'start',
  },
  planCard: {
    background: '#fff',
    border: '1.5px solid #e5e7eb',
    borderRadius: '16px',
    padding: '32px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  planCardHighlight: {
    background: 'linear-gradient(135deg, #1e3a8a, #1e40af)',
    border: 'none',
    boxShadow: '0 20px 40px rgba(30,64,175,0.3)',
    transform: 'scale(1.04)',
  },
  planPopular: {
    position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
    background: '#f97316',
    color: '#fff', fontSize: '11px', fontWeight: '700',
    padding: '3px 14px', borderRadius: '12px',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  planName: { fontSize: '18px', fontWeight: '700', color: '#111827' },
  planPriceRow: { display: 'flex', alignItems: 'baseline', gap: '4px' },
  planPrice: { fontSize: '36px', fontWeight: '800', color: '#111827', letterSpacing: '-1px' },
  planPriceWhite: { color: '#fff' },
  planPeriod: { fontSize: '14px', color: '#9ca3af' },
  planDesc: { fontSize: '13px', color: '#6b7280', marginBottom: '4px' },
  planDescWhite: { color: 'rgba(255,255,255,0.7)' },
  planFeatures: { listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 },
  planFeature: { fontSize: '14px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' },
  planFeatureWhite: { color: 'rgba(255,255,255,0.85)' },
  planCheck: { color: '#16a34a', fontWeight: '700' },
  planCheckWhite: { color: '#86efac' },
  planCta: {
    display: 'block',
    textAlign: 'center',
    padding: '12px',
    background: '#f3f4f6',
    color: '#1e40af',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '700',
    textDecoration: 'none',
    marginTop: '8px',
    transition: 'background 0.15s',
  },
  planCtaHighlight: {
    background: '#fff',
    color: '#1e40af',
  },

  // CTA section
  ctaSection: {
    background: 'linear-gradient(135deg, #0f172a, #1e3a8a)',
    padding: '96px 24px',
  },
  ctaBox: { textAlign: 'center' },
  ctaTitle: { fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: '800', color: '#fff', letterSpacing: '-1px', marginBottom: '16px' },
  ctaDesc: { fontSize: '18px', color: 'rgba(255,255,255,0.7)', marginBottom: '36px' },
  ctaBtns: { display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap' },
  ctaBtnPrimary: {
    padding: '14px 32px',
    background: '#f97316',
    color: '#fff',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '700',
    textDecoration: 'none',
  },
  ctaBtnSecondary: {
    padding: '14px 32px',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    color: '#fff',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  // Contact
  contactLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '64px',
    alignItems: 'start',
  },
  contactInfo: {},
  contactItems: { display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '28px' },
  contactItem: { display: 'flex', alignItems: 'flex-start', gap: '14px' },
  contactItemIcon: { fontSize: '22px', marginTop: '2px' },
  contactItemLabel: { fontSize: '12px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' },
  contactItemValue: { fontSize: '15px', color: '#111827', marginTop: '2px' },
  contactFormCard: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '16px',
    padding: '36px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
  },
  contactFormTitle: { fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '20px' },
  contactForm: { display: 'flex', flexDirection: 'column', gap: '16px' },
  contactField: { display: 'flex', flexDirection: 'column', gap: '6px' },
  contactLabel: { fontSize: '13px', fontWeight: '500', color: '#374151' },
  contactInput: {
    padding: '10px 14px',
    border: '1.5px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    background: '#f9fafb',
    color: '#111827',
  },
  contactSubmit: {
    padding: '12px',
    background: 'linear-gradient(135deg, #1e40af, #2563eb)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: 'inherit',
    marginTop: '4px',
  },
  contactSuccess: { textAlign: 'center', padding: '40px 20px' },
  contactSuccessIcon: { fontSize: '48px', display: 'block', marginBottom: '16px' },
  contactSuccessTitle: { fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '8px' },
  contactSuccessDesc: { fontSize: '14px', color: '#6b7280' },
};
